import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import type { IPortal } from '@mcn-platform/models';
import { getWizardsFromConfig, type SelectionWizardConfig } from '@mcn-platform/converse-lib';
import { WizardEditor, defaultNewWizard, mergePortalClientAIConfigWithWizards } from './WizardToolQuestionsField';

export type SelectionWizardToolFieldProps = {
  portal: IPortal;
  configuration?: Record<string, any>;
  onConfigChange?: (field: string, value: any) => void;
};

const persistWizards = (portal: IPortal, wizards: SelectionWizardConfig[],
  onConfigChange?: (field: string, value: any) => void) => {
  onConfigChange?.('wizards', wizards);
  const config = mergePortalClientAIConfigWithWizards(portal, wizards);
  portal.updateFields(['clientAIConfig'], { clientAIConfig: config } as never);
};

const SelectionWizardToolField: React.FC<SelectionWizardToolFieldProps> = ({ portal, configuration, onConfigChange }) => {
  const [wizards, setWizards] = useState<SelectionWizardConfig[]>(() =>
    getWizardsFromConfig(configuration || portal?.clientAIConfig || undefined, Boolean(onConfigChange)),
  );
  useEffect(() => {
    setWizards(getWizardsFromConfig(configuration || portal?.clientAIConfig || undefined, Boolean(onConfigChange)));
  }, [portal?.id, portal?.clientAIConfig, portal?.syncState, configuration, onConfigChange]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [localWizard, setLocalWizard] = useState<SelectionWizardConfig | null>(null);

  const openAdd = useCallback(() => {
    setEditingIndex(null);
    setLocalWizard(defaultNewWizard());
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((index: number) => {
    setEditingIndex(index);
    const copy = JSON.parse(JSON.stringify(wizards[index]));
    setLocalWizard(onConfigChange ? getWizardsFromConfig({ wizards: [copy] }, true)[0] : copy);
    setModalOpen(true);
  }, [wizards, onConfigChange]);

  const handleCancel = useCallback(() => {
    setModalOpen(false);
    setLocalWizard(null);
    setEditingIndex(null);
  }, []);

  const handleUpdate = useCallback(() => {
    if (!localWizard || !portal) return;
    const next = wizards.slice();
    if (editingIndex !== null && editingIndex >= 0) {
      next[editingIndex] = localWizard;
    } else {
      next.push(localWizard);
    }
    setWizards(next);
    persistWizards(portal, next, onConfigChange);
    setModalOpen(false);
    setLocalWizard(null);
    setEditingIndex(null);
  }, [localWizard, portal, editingIndex, wizards, onConfigChange]);

  const handleRemove = useCallback((index: number) => {
    const next = wizards.filter((_, i) => i !== index);
    setWizards(next);
    persistWizards(portal, next, onConfigChange);
  }, [wizards, portal, onConfigChange]);

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Selection Wizard Tool
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Preference wizards (e.g. shoes, runner&apos;s watch). Each wizard is an entry below; edit in a modal with
        Cancel or Update.
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button size="small" variant="outlined" startIcon={<Add />} onClick={openAdd}>
          Add wizard
        </Button>
      </Stack>
      <List dense disablePadding sx={{ bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
        {wizards.map((wizard, index) => wizard && typeof wizard === 'object' && !Array.isArray(wizard) ? (
          <ListItem
            key={wizard.id}
            secondaryAction={
              <>
                <IconButton edge="end" size="small" onClick={() => openEdit(index)} aria-label="Edit wizard">
                  <Edit />
                </IconButton>
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => handleRemove(index)}
                  aria-label="Remove wizard"
                >
                  <Delete />
                </IconButton>
              </>
            }
            disablePadding
          >
            <ListItemButton onClick={() => openEdit(index)}>
              <ListItemText primary={wizard.name || wizard.id || 'New wizard'} secondary={wizard.id} />
            </ListItemButton>
          </ListItem>
        ) : null)}
      </List>

      <Dialog
        open={modalOpen}
        onClose={handleCancel}
        scroll="paper"
        PaperProps={{ sx: { width: '90%', maxWidth: '90%' } }}
      >
        <DialogTitle>{editingIndex !== null && editingIndex >= 0 ? 'Edit wizard' : 'Add wizard'}</DialogTitle>
        <DialogContent dividers>
          {localWizard && <WizardEditor wizard={localWizard} onChange={(next) => setLocalWizard(
            onConfigChange ? getWizardsFromConfig({ wizards: [next] }, true)[0] : next,
          )} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SelectionWizardToolField;
